import { NewCalendarIcon } from '@commenda/icons/new/calendar';
import { cn, NonTimezoneSensitiveDate } from '@commenda/utils';
import React from 'react';
import { isMatch, Matcher } from 'react-day-picker';
import { useToggle } from '../../hooks/useToggle';
import { Calendar } from '../../internal/Calendar';
import { Button } from '../Button';
import { Input } from '../Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

interface DatePickerProps {
  /** Goes on the default trigger only, not a custom `renderTrigger`. */
  id?: string;
  date: string | undefined;
  setDate: (dt: string | undefined) => void;
  showOutsideDays?: boolean;
  showWeekNumber?: boolean;
  placeholder?: string;
  _triggerClassName?: string;
  _popoverClassName?: string;
  renderTrigger?: (props: {
    date: string | undefined;
    onClick: () => void;
    isOpen: boolean;
  }) => React.ReactNode;
  disabled?: Matcher | Matcher[] | undefined;
  disableInput?: boolean;
  hideCalendar?: boolean;
  hasError?: boolean;
  validateDate?: (date: string) => boolean;
}

// Helper function to find the nearest scrollable container
const findScrollableContainer = (
  element: HTMLElement | null
): HTMLElement | null => {
  if (!element) return null;

  let current: HTMLElement | null = element;

  while (current) {
    const { overflowY, overflow } = window.getComputedStyle(current);

    // Check if element is scrollable
    if (
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflow === 'auto' ||
        overflow === 'scroll') &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }

    // Stop at dialog content to avoid going outside the modal
    if (
      current.hasAttribute('role') &&
      current.getAttribute('role') === 'dialog'
    ) {
      break;
    }

    current = current.parentElement;
  }

  return null;
};

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  function DatePicker(
    {
      id,
      date,
      setDate,
      showOutsideDays,
      showWeekNumber,
      placeholder,
      _triggerClassName = '',
      _popoverClassName = '',
      renderTrigger,
      disabled,
      disableInput,
      hideCalendar,
      hasError,
      validateDate,
    },
    forwardedRef
  ) {
  const datePickerToggle = useToggle();
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const triggerButtonRef = React.useRef<HTMLButtonElement>(null);
  const typedDateInputId = React.useId();

  // Kept for popover placement; the parent gets the same node.
  React.useImperativeHandle<
    HTMLButtonElement | null,
    HTMLButtonElement | null
  >(forwardedRef, () => triggerButtonRef.current);

  // Convert ISO string to NTS date for internal use
  const getNTSDate = (
    isoString: string | undefined
  ): NonTimezoneSensitiveDate | undefined => {
    if (!isoString) {
      // return today's date
      return NonTimezoneSensitiveDate.fromNativeDate(new Date());
    }
    return NonTimezoneSensitiveDate.fromISO8601String(isoString);
  };

  // State for the input field - maintain as ISO string

  // Use NTS date for calendar month state
  const ntsDate = getNTSDate(date);
  const [localDate, setLocalDate] = React.useState<string>(
    ntsDate ? ntsDate.toISO8601String() : ''
  );

  const [month, setMonth] = React.useState<Date>(
    ntsDate ? ntsDate.toDate() : new Date()
  );

  const adjustPosition = () => {
    if (!popoverRef.current) return;

    const viewportHeight = window.innerHeight;

    popoverRef.current.style.transform = '';

    const updatedRect = popoverRef.current.getBoundingClientRect();

    if (updatedRect.bottom > viewportHeight) {
      const overflowBottom = updatedRect.bottom - viewportHeight;
      popoverRef.current.style.transform = `translateY(-${overflowBottom + 10}px)`;
    }

    if (updatedRect.top < 0) {
      popoverRef.current.style.transform = `translateY(${Math.abs(updatedRect.top) + 10}px)`;
    }
  };

  React.useEffect(() => {
    // custom hook to adjust the position of the popover to avoid it from being cut off
    if (datePickerToggle.value) {
      const timer = setTimeout(() => {
        adjustPosition();
      }, 10);

      const observer = new ResizeObserver(() => {
        adjustPosition();
      });

      if (popoverRef.current) {
        observer.observe(popoverRef.current);
      }

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
    return undefined;
  }, [datePickerToggle.value, month]);

  React.useEffect(() => {
    // Disable scrolling on the scrollable container when popover is open
    const container = triggerButtonRef.current
      ? findScrollableContainer(triggerButtonRef.current)
      : null;

    if (container) {
      if (datePickerToggle.value) {
        // Store original overflow value to restore it later
        const originalOverflow = container.style.overflow;
        container.style.overflow = 'hidden';
        return () => {
          container.style.overflow = originalOverflow || 'auto';
        };
      }
    }
    return undefined;
  }, [datePickerToggle.value]);

  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    // Convert native Date to NTS then to ISO string
    const selectedNtsDate =
      NonTimezoneSensitiveDate.fromNativeDate(selectedDate);
    const dateStr = selectedNtsDate.toISO8601String();

    setLocalDate(dateStr);
    setMonth(selectedDate);
    setDate(dateStr);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length > 10) return;
    setLocalDate(newValue);
  };

  const isDateInDisabledRange = (dateString: string) => {
    if (!dateString) return true;
    const parsedNtsDate =
      NonTimezoneSensitiveDate.fromISO8601String(dateString);
    if (disabled) {
      if (Array.isArray(disabled)) {
        return isMatch(parsedNtsDate.toDate(), disabled);
      }
      return isMatch(parsedNtsDate.toDate(), [disabled]);
    }
    return false;
  };

  const onApplyDate = () => {
    if (!localDate) return;
    try {
      // as the type is string, we need to convert it to NTS date,
      // at the same time, we need to ensure that it is a valid date format
      // otherwise, this will fail and break the whole application.
      const parsedNtsDate =
        NonTimezoneSensitiveDate.fromISO8601String(localDate);
      if (
        (validateDate && !validateDate(localDate)) ||
        isDateInDisabledRange(localDate)
      ) {
        setDate(undefined);
        return;
      }
      setMonth(parsedNtsDate.toDate());
      setDate(localDate);
      datePickerToggle.off();
    } catch (error) {
      console.error('Invalid date format.');
    }
  };

  const getDisplayDate = (dateString: string | undefined): string => {
    if (!dateString) return placeholder || 'Select date';
    const displayNtsDate = getNTSDate(dateString);
    return displayNtsDate
      ? displayNtsDate.format('MMM DD, YYYY')
      : 'Invalid Date';
  };

  return (
    <Popover
      modal
      open={datePickerToggle.value}
      onOpenChange={(open) => {
        if (!open) datePickerToggle.off();
        else datePickerToggle.on();
      }}
    >
      <PopoverTrigger asChild>
        {renderTrigger ? (
          renderTrigger({
            date,
            onClick: datePickerToggle.on,
            isOpen: datePickerToggle.value,
          })
        ) : (
          <Button
            id={id}
            ref={triggerButtonRef}
            disabled={disableInput}
            variant="secondary"
            className={cn(
              'flex !h-10 w-full box-border items-center justify-between rounded-md px-xl py-2.5 bg-bg-primary text-text-primary txt-small-regular ring-0 transition-colors',
              'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
              'border disabled:border-border-disabled hover:bg-bg-primary',
              hasError
                ? 'border-border-error hover:border-border-error focus-visible:border-border-error-hover focus-visible:ring-border-error-hover'
                : 'border-border-primary hover:border-border-primary-hover',
              _triggerClassName
            )}
            onClick={datePickerToggle.on}
          >
            <div className="flex items-center gap-2">
              <NewCalendarIcon className="size-4 shrink-0 text-fg-quaternary" />
              <span
                className={cn(
                  'line-clamp-1 txt-small-regular',
                  // Match Input's placeholder color when no date is selected.
                  !date && 'text-text-placeholder',
                )}
              >
                {getDisplayDate(date)}
              </span>
            </div>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', _popoverClassName)}
        asChild
        align="start"
        sideOffset={4}
        avoidCollisions
      >
        <div
          ref={popoverRef}
          className="rounded-xl border border-border-primary shadow-lg bg-bg-primary w-fit flex flex-col"
        >
          {!hideCalendar ? (
            <div className="py-2.5 px-1.5">
              <Calendar
                mode="single"
                selected={ntsDate?.toDate()}
                onSelect={handleCalendarSelect}
                defaultMonth={ntsDate?.toDate()}
                initialFocus
                month={month}
                today={new Date()}
                onMonthChange={setMonth}
                showOutsideDays={showOutsideDays}
                showWeekNumber={showWeekNumber}
                disabled={disabled}
              />
            </div>
          ) : null}
          <div className="border-t p-4 grid grid-cols-2 gap-4">
            <Input
              id={typedDateInputId}
              aria-label="Date"
              type="date"
              value={localDate}
              onChange={handleInputChange}
              disabled={typeof disabled === 'boolean' ? disabled : false}
              placeholder="DD/MM/YYYY"
            />
            <Button
              variant="primary"
              className="h-auto w-full"
              size="sm"
              disabled={!localDate || isDateInDisabledRange(localDate)}
              onClick={onApplyDate}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

DatePicker.displayName = 'DatePicker';

export { DatePicker };
